import type { GisScope } from '../api/types'

const iso = (d: Date): string => d.toISOString().slice(0, 10)

function daysAgo(today: Date, n: number): string {
  const d = new Date(today)
  d.setUTCDate(d.getUTCDate() - n)
  return iso(d)
}

function fyToDate(today: Date): { date_from: string; date_to: string } {
  const year = today.getUTCMonth() >= 3 ? today.getUTCFullYear() : today.getUTCFullYear() - 1
  return { date_from: `${year}-04-01`, date_to: iso(today) }
}

const PERIOD_DAYS: Record<'7d' | '30d' | '90d', number> = { '7d': 7, '30d': 30, '90d': 90 }

/**
 * The reports endpoints accept date_from/date_to, so we convert the GIS period enum (which differs
 * from the reports' today|week|month|quarter|custom vocabulary) into a concrete range. Uses pure UTC
 * date math for deterministic, timezone-independent windows; `today` is injectable for tests.
 * (Note: gisApi.ts's issuesDateRange does the same conceptually but with local-clock math — these are
 * intentionally NOT shared yet; consolidating them is a tracked follow-up.)
 */
export function periodToDateRange(scope: GisScope, today: Date = new Date()): { date_from?: string; date_to?: string } {
  if (scope.period === 'fy') {
    return fyToDate(today)
  }
  if (scope.period === 'custom') {
    return scope.dateFrom && scope.dateTo ? { date_from: scope.dateFrom, date_to: scope.dateTo } : {}
  }
  return { date_from: daysAgo(today, PERIOD_DAYS[scope.period]), date_to: iso(today) }
}

/**
 * Full query-param set shared by every report's JSON fetch AND its xlsx export, so the two can never
 * drift: the resolved date range + entity_id (when filtered) + mandal (when a mandal is selected).
 * Report-specific filters (survey_status, status, status_group, paging, format) are added by the caller.
 */
export function reportsScopeParams(
  scope: GisScope,
  opts: { mandal?: string | undefined } = {},
  today: Date = new Date(),
): Record<string, string> {
  const range = periodToDateRange(scope, today)
  return {
    ...range,
    // The reports endpoints use the collector period vocabulary (today|week|month|quarter|custom) and
    // IGNORE bare date_from/date_to — falling back to a 30-day window — unless period=custom is sent
    // alongside them (backend handoff §8). So we send period=custom whenever we have a concrete range,
    // making every GIS period (7d/90d/FY/custom) honour its exact window instead of silently showing 30d.
    ...(range.date_from && range.date_to ? { period: 'custom' } : {}),
    ...(scope.entityId ? { entity_id: scope.entityId } : {}),
    ...(opts.mandal ? { mandal: opts.mandal } : {}),
  }
}
